import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { Styles } from '@t2c/shared-fe';
import Typography from '@mui/material/Typography';
import { Status, WebSocketDataReturnType } from '../../../../@types';
import { VideoPlayerForStockVideos } from '../../../../components/video-player/VideoPlayerForStockVideos';
import { useGetBrollMediaProgress } from '../../../../hooks';
import { StatusWithProgressBar } from '../../../../Common';
import { STATUS_REGISTRATION_REQUIREMENT } from '../../../../utils';

interface Props {
  mediaName?: string;
  mediaUrl: string;
  isImage: boolean;
  isSelected: boolean;
  handleOnSelectMedia: () => void;
  handleOnDelete: () => void;
  id: string;
  status: string;
}

export const BrollMediaCard: React.FC<Props> = ({
  id,
  mediaUrl,
  mediaName,
  isImage,
  isSelected,
  handleOnSelectMedia,
  handleOnDelete,
  status,
}) => {
  const { resourceUrl, resourceStatus, progressPercentage } = useGetBrollMediaProgress({
    id,
    initialResourceUrl: mediaUrl,
    initialResourceStatus: status,
    type: WebSocketDataReturnType.AI_GENERATE,
  });
  const isError = resourceStatus === Status.Error;
  const { registrationRequired } = STATUS_REGISTRATION_REQUIREMENT;
  const isInProgress = registrationRequired.includes(resourceStatus);

  const isSuccess = resourceStatus === Status.Success;

  return (
    <Tooltip sx={{ color: 'white' }} title={mediaName} enterDelay={0} leaveDelay={0}>
      <Box
        onClick={() => {
          if (isSuccess) handleOnSelectMedia();
        }}
        sx={{ ...styles.card, borderWidth: isSelected ? 4 : 0 }}
      >
        {isInProgress && (
          <Box sx={{ width: '80%' }}>
            <StatusWithProgressBar
              progressPercentage={progressPercentage}
              status={resourceStatus}
              isBrollCaption
            />
          </Box>
        )}
        {isError && (
          <Typography sx={{ color: (theme) => theme.palette.text.error }}>Failed!</Typography>
        )}

        {!isImage && isSuccess && (
          <Box sx={{ width: '100%' }}>
            <VideoPlayerForStockVideos height="142px" url={resourceUrl} />
          </Box>
        )}

        {isImage && isSuccess && (
          <Box sx={{ height: '100%', width: '100%' }}>
            <img
              src={resourceUrl}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
        )}

        {!isInProgress && (
          <DeleteIcon
            color="disabled"
            sx={{
              position: 'absolute',
              top: 1,
              right: 1,
              '&:hover': { color: (theme) => theme.palette.background.deleteBtn },
            }}
            onClick={handleOnDelete}
          />
        )}
      </Box>
    </Tooltip>
  );
};

const styles: Styles = {
  cardContainer: {
    width: '100%',
    height: '100%',
    border: '2px solid',
    borderColor: 'transparent',
    cursor: 'pointer',
    '&:hover': {
      borderColor: (theme) => theme.palette.primary.main,
    },
  },
  card: {
    height: 200,
    width: 210,
    borderWidth: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderColor: (theme) => theme.palette.text.coloredText,
    backgroundColor: (theme) => theme.palette.common.black,
    borderRadius: '10px',
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative',
  },
};
